import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaExchangeAlt, FaMapMarkerAlt, FaSnowflake, FaUsers, FaCarSide } from "react-icons/fa";
import Button from "../../Components/UI/Button";
import { Card, Badge, SectionHeading } from "../../Components/UI/Card";
import { StarDisplay } from "../../Components/Marketplace/StarRating";
import { getServices, getApiErrorMessage } from "../../Components/Marketplace/services/marketplaceApi";
import { formatINR } from "../../Components/Marketplace/marketplaceUtils";

const PLACEHOLDER_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='260'><rect width='100%' height='100%' fill='%23f6f7f9'/><text x='50%' y='50%' font-family='sans-serif' font-size='16' fill='%238993a4' text-anchor='middle' dominant-baseline='middle'>Bachatwali Ticket</text></svg>`
  );

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

// Dedicated FROM/TO/DATE/TIME/PASSENGERS Taxi search entry point (spec section 2) — separate
// from the generic multi-category MarketplaceBrowsePage, matching Flight/Hotel/Bus search UX.
// Real partner inventory only: hits the same getServices({category:'Taxi'}) endpoint the
// marketplace browse page uses, never fabricated results.
const TaxiSearchPage = () => {
  const navigate = useNavigate();

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [sort, setSort] = useState("newest");

  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runSearch = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!from.trim()) {
        setError("Please enter a pickup city/area.");
        return;
      }
      setLoading(true);
      setError(null);
      setSearched(true);
      try {
        const res = await getServices({
          category: "Taxi",
          destination: from.trim(),
          minSeats: passengers || undefined,
          sort,
          limit: 24,
        });
        setResults(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to search taxis."));
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [from, passengers, sort]
  );

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const goToDetail = (service) => {
    navigate(`/marketplace/services/${service._id}`, {
      state: {
        prefill: {
          pickupLocation: from.trim(),
          dropLocation: to.trim(),
          travelDate: date,
          passengers,
        },
      },
    });
  };

  return (
    <div className="min-h-screen bg-ink-50/40">
      <div className="bg-gradient-to-br from-brand-600 to-brand-800 text-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-brand-100 mb-2">
            Bachatwali Ticket Taxi
          </p>
          <h1 className="text-2xl sm:text-[32px] font-extrabold tracking-tight mb-6">
            Book a real taxi from verified partners
          </h1>

          <form
            onSubmit={runSearch}
            className="bg-white rounded-2xl p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end"
          >
            <div className="lg:col-span-2 relative">
              <label className="block text-xs font-semibold text-ink-500 mb-1">From</label>
              <input
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="Pickup city / area"
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
              <button
                type="button"
                onClick={swap}
                aria-label="Swap from and to"
                className="hidden lg:flex absolute -right-4 top-8 h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white shadow"
              >
                <FaExchangeAlt size={12} />
              </button>
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-semibold text-ink-500 mb-1">To</label>
              <input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="Drop city / area"
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-500 mb-1">Date</label>
              <input
                type="date"
                min={todayISO()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-500 mb-1">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-500 mb-1">Passengers</label>
              <input
                type="number"
                min={1}
                value={passengers}
                onChange={(e) => setPassengers(Number(e.target.value) || 1)}
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-6">
              <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto" loading={loading}>
                Search Taxis
              </Button>
            </div>
          </form>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8">
        {!searched ? (
          <div className="text-center py-16 text-ink-500">
            <p className="text-lg font-semibold text-ink-700 mb-1">Enter a pickup city to search</p>
            <p className="text-sm">Real taxis from verified Bachatwali Ticket partners will show up here.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <SectionHeading
                title="Available taxis"
                subtitle={loading ? "Searching..." : `${results.length} taxi${results.length === 1 ? "" : "s"} found`}
              />
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  runSearch();
                }}
                className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-brand-50 border border-brand-200 text-brand-700 text-sm flex items-center justify-between">
                <span>{error}</span>
                <Button variant="secondary" size="sm" onClick={runSearch}>
                  Retry
                </Button>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-64 rounded-2xl bg-ink-100 animate-pulse" />
                ))}
              </div>
            ) : results.length === 0 && !error ? (
              <div className="text-center py-16 text-ink-500">
                <p className="text-lg font-semibold text-ink-700 mb-1">No taxis found for "{from}"</p>
                <p className="text-sm">Try a nearby city, or reduce the passenger count.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {results.map((service) => {
                  const taxi = service.taxiDetails || {};
                  const image = (service.photos || []).find(Boolean) || PLACEHOLDER_IMG;
                  const partner = service.partnerId || {};
                  return (
                    <Card
                      key={service._id}
                      hover
                      className="overflow-hidden cursor-pointer flex flex-col"
                      onClick={() => goToDetail(service)}
                    >
                      <div className="relative h-40 w-full overflow-hidden bg-ink-50">
                        <img
                          src={image}
                          alt={service.title}
                          className="h-full w-full object-cover"
                          onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                        />
                        {taxi.vehicleType && (
                          <div className="absolute top-3 left-3">
                            <Badge tone="dark">{taxi.vehicleType}</Badge>
                          </div>
                        )}
                      </div>
                      <div className="p-4 flex flex-col gap-2 flex-1">
                        <h3 className="font-bold text-ink-900 text-[15px] leading-snug line-clamp-2">
                          {service.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-ink-500">
                          {!!taxi.seatingCapacity && (
                            <span className="flex items-center gap-1">
                              <FaUsers /> {taxi.seatingCapacity}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <FaSnowflake /> {taxi.isAC ? "AC" : "Non-AC"}
                          </span>
                          {taxi.vehicleModel && (
                            <span className="flex items-center gap-1">
                              <FaCarSide /> {taxi.vehicleModel}
                            </span>
                          )}
                        </div>
                        <p className="flex items-center gap-1.5 text-xs text-ink-500 truncate">
                          <FaMapMarkerAlt className="shrink-0" />
                          {partner.businessName || "Bachatwali Ticket Partner"}
                          {partner.city ? ` · ${partner.city}` : ""}
                        </p>
                        {(partner.ratingCount || 0) > 0 && (
                          <StarDisplay value={partner.ratingAvg} count={partner.ratingCount} />
                        )}
                        <div className="mt-auto pt-2 flex items-end justify-between">
                          <div>
                            <p className="text-[11px] text-ink-400 uppercase tracking-wide font-semibold">
                              Starting at
                            </p>
                            <p className="text-lg font-extrabold text-brand-600">{formatINR(service.basePrice)}</p>
                          </div>
                          <span className="text-xs font-semibold text-brand-600">Select →</span>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaxiSearchPage;
